import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import upperFirst from 'lodash/upperFirst';
import lowerCase from 'lodash/lowerCase';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Tooltip, Popconfirm, Modal, Button } from 'antd';
import SvgFormat from '~helpers/svgFormat';
import { getSymbolFromCurrency } from '~helpers/currency';
import { commissionStatus, commissionDetailsCategories } from '~constants/commission';
import * as commissionActions from '~actions/pms-actions/properties/commission';
import showElementByAuth from '~helpers/auth';
import { platformEntity, entityAction } from '~constants';

import detailsTab from './commission-details';

const mapDispatchToProps = dispatch => ({
  deleteCommission: input => {
    dispatch(commissionActions.deleteCommission(input));
  },
});
@connect(null, mapDispatchToProps)
export default class CommissionList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCard: 3,
      showModal: false,
      commissionDetails: [],
    };
    this.timeDom = [];
  }

  componentDidMount() {
    this.showCardNumber();
    window.addEventListener('resize', this.showCardNumber);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.showCardNumber);
  }

  showCardNumber = () => {
    const dashboardLayout = document.querySelector('.dashboard__layout');
    const dashboardLayoutWidth = dashboardLayout ? dashboardLayout.clientWidth : 0;
    if (dashboardLayoutWidth >= 1063) {
      this.setState({
        showCard: 3,
      });
    }

    if (dashboardLayoutWidth < 1063 && dashboardLayoutWidth > 724) {
      this.setState({
        showCard: 2,
      });
    }
  };

  showUpdate = updatedAt => (
    <React.Fragment>
      <span>{this.props.t('cms:cms.property.commission.list.updated_time')}:&nbsp;</span>
      <span className="commission-list__update-time">
        {moment(updatedAt).format('DD/MM/YYYY HH:mm:ss')}
      </span>
    </React.Fragment>
  );

  sortCommission = commission => {
    if (commission.length === 0) {
      return [];
    }

    const statusSortArr = [
      commissionStatus.ACTIVE,
      commissionStatus.INACTIVE,
      commissionStatus.EXPIRE,
    ];

    return [...commission].sort((a, b) => {
      if (a.status === b.status) {
        return moment(a.updatedAt).isBefore(moment(b.updatedAt));
      }

      return statusSortArr.indexOf(a.status) - statusSortArr.indexOf(b.status);
    });
  };

  handleDeleteCommission = id => {
    this.props.deleteCommission({ id });
  };

  handleDateFormat = date => {
    if (date) {
      return moment(date).format('MMM D, YYYY');
    }
    return '-';
  };

  handleFormatData = commissionTier => {
    const { t, currency } = this.props;
    const commissionTierDetails = Object.assign({}, commissionTier);
    // Commission Category
    commissionTierDetails.category = t(commissionDetailsCategories[commissionTier.category]);
    // Set as bonus
    commissionTierDetails.bonus = commissionTier.bonus
      ? t('cms:cms.form.pop_confirm.yes')
      : t('cms:cms.form.pop_confirm.no');
    // Commission name
    commissionTierDetails.name = commissionTier.name;
    // Commission type
    commissionTierDetails.calculation_type = upperFirst(lowerCase(commissionTier.type));
    // Commission value
    commissionTierDetails.calculation_value =
      commissionTier.type === 'PERCENTAGE'
        ? `${commissionTier.value}%`
        : commissionTier.value + currency;
    // Coverted commission
    commissionTierDetails.convert_commission = commissionTier.convertedCalculationValue;
    // Effective from
    commissionTierDetails.effective_from = this.handleDateFormat(commissionTier.effectiveFrom);
    // Effective to
    commissionTierDetails.effective_to = this.handleDateFormat(commissionTier.effectiveTo);
    // Tenancy length range
    commissionTierDetails.tenancy_length =
      (commissionTier.tenancyLengthFrom || commissionTier.tenancyLengthTo) &&
      `${commissionTier.tenancyLengthFrom || ''} ~ ${commissionTier.tenancyLengthTo || ''}`;
    // From check in date
    commissionTierDetails.check_in_date_from = this.handleDateFormat(
      commissionTier.checkInDateFrom,
    );
    // Tenancy unit
    commissionTierDetails.tenancy_unit = lowerCase(commissionTier.tenancyUnit);
    // To check in date
    commissionTierDetails.check_in_date_to = this.handleDateFormat(commissionTier.checkInDateTo);
    // To booking count
    commissionTierDetails.booking_count_to = commissionTier.bookingCountTo;
    // From booking count
    commissionTierDetails.booking_count_from = commissionTier.bookingCountFrom;
    // Commission cap type
    commissionTierDetails.commission_cap_type = lowerCase(commissionTier.capType);
    // Commission Cap
    commissionTierDetails.commission_cap = commissionTier.capValue;
    // Application to Rooms
    commissionTierDetails.application_to_rooms = commissionTier.unitTypes;
    // Commission not entirely covered
    commissionTierDetails.commission_not_entirely_covered =
      commissionTier.fullyCalculatable && commissionTier.fullyCalculatable
        ? t('cms:cms.form.pop_confirm.yes')
        : t('cms:cms.form.pop_confirm.no');
    // Retrospective commission
    commissionTierDetails.retrospective_commission = commissionTier.retrospectiveCommission;
    // Commission structure description
    commissionTierDetails.commission_structure_description = commissionTier.description;

    const details = detailsTab(commissionTierDetails);

    this.setState({
      commissionDetails: details,
    });
  };

  // control modal open or close
  handleChangeModal = (modalStatus, commissionTier = []) => {
    this.handleFormatData(commissionTier);
    this.setState({
      showModal: modalStatus,
    });
  };

  render() {
    const { showCard, showModal, commissionDetails } = this.state;
    const { t } = this.props;

    return (
      <div className="commission-list">
        <If
          condition={showElementByAuth(
            platformEntity.COMMISSION_COMISSION_TIERS,
            entityAction.CREATE,
          )}
        >
          <div
            className={classNames('commission-list__card commission-list__add', {
              'commission-list__second': showCard === 2,
            })}
          >
            <div
              className="commission-list__add-content"
              onClick={() => {
                this.props.openModal('addNew');
              }}
              role="button"
              tabIndex="0"
            >
              <SvgFormat type="commission-add" className="commission-list__add-icon" />
              <p>{t('cms:cms.property.commission.create.create_commission_modal.add.title')}</p>
            </div>
          </div>
        </If>
        <For
          index="index"
          of={this.sortCommission(this.props.commissionTier)}
          each="commissionTier"
        >
          <div
            key={index}
            className={classNames('commission-list__card', {
              'commission-list__second': showCard === 2,
            })}
          >
            <div
              className={classNames(
                'commission-list__status',
                {
                  'commission-list__status--active':
                    commissionTier.status === commissionStatus.ACTIVE,
                },
                {
                  'commission-list__status--expire':
                    commissionTier.status === commissionStatus.EXPIRE,
                },
              )}
            >
              <If condition={commissionTier.status}>
                &nbsp;
                {t(
                  `cms:cms.property.commission.list.status.${commissionTier.status.toLowerCase()}`,
                )}
              </If>
            </div>
            <div
              className={classNames('commission-list__icon-box', {
                'commission-list__icon-box--grey':
                  commissionTier.status === commissionStatus.EXPIRE,
              })}
            >
              <If condition={commissionTier.category}>
                <SvgFormat
                  type={commissionTier.category.toLowerCase().replace('_', '-')}
                  className={classNames('commission-list__list-icon', {
                    'commission-list__list-icon--rebookers':
                      commissionTier.category === 'REBOOKERS_COMMISSION',
                  })}
                />
              </If>
            </div>
            <div className="commission-list__text">
              <div className="commission-list__category">
                <If condition={commissionTier.category}>
                  {t(
                    `cms:cms.property.commission.create.commission_type.${commissionTier.category.toLowerCase()}`,
                  )}
                </If>
              </div>
              <div className="commission-list__great-commission">{commissionTier.name}</div>
              <div className="commission-list__percentage">
                <If condition={commissionTier.type === 'VALUE'}>
                  {`${getSymbolFromCurrency(this.props.currency)}${commissionTier.value}`}
                </If>
                <If condition={commissionTier.type === 'PERCENTAGE'}>
                  {`${commissionTier.value}%`}
                </If>
                <If condition={commissionTier.type === 'UNIT_PRICE'}>
                  {`${commissionTier.value}`}
                  <span className="commission-list__unit-price-info">
                    {t('cms:cms.property.commission.list.unit_price_commission')}
                  </span>
                </If>
                <If condition={commissionTier.bonus}>
                  <div className="commission-list__bonus">
                    <div className="commission-list__bonus-text">
                      {t('cms:cms.property.commission.list.bonus')}
                    </div>
                    <SvgFormat type="commission-bonus" className="commission-list__bonus-icon" />
                  </div>
                </If>
              </div>
              <If condition={commissionTier.category === 'NUM_BOOKINGS'}>
                <div className="commission-list__booking-number">
                  {t('cms:cms.property.commission.list.booking_numbers')}:&nbsp;
                  <If condition={commissionTier.bookingCountTo}>
                    <span>
                      {commissionTier.bookingCountFrom}
                      ~&nbsp;{commissionTier.bookingCountTo}
                    </span>
                  </If>
                  <If condition={!commissionTier.bookingCountTo}>
                    <span>
                      {commissionTier.bookingCountFrom}{' '}
                      {t('cms:cms.property.commission.list.more_than')}
                    </span>
                  </If>
                </div>
              </If>
              <If condition={commissionTier.category === 'TENANCY_LENGTH'}>
                <div className="commission-list__booking-number">
                  {t('cms:cms.property.commission.list.tenancy_length')}:&nbsp;
                  <If condition={commissionTier.tenancyLengthTo}>
                    <span>
                      {`${commissionTier.tenancyLengthFrom} ≤ Length < ${commissionTier.tenancyLengthTo}`}
                      {commissionTier.tenancyUnit
                        ? `(${commissionTier.tenancyUnit.toLowerCase()})`
                        : null}
                    </span>
                  </If>
                  <If condition={!commissionTier.tenancyLengthTo}>
                    <span>
                      {commissionTier.tenancyLengthFrom}&nbsp;
                      {t('cms:cms.property.commission.list.more_than')}&nbsp;&nbsp;
                      {commissionTier.tenancyUnit
                        ? `(${commissionTier.tenancyUnit.toLowerCase()})`
                        : null}
                    </span>
                  </If>
                </div>
              </If>
              <div className="commission-list__effective-dates">
                {`${t('cms:cms.property.commission.list.effective_dates')}: ${moment(
                  commissionTier.effectiveFrom,
                ).format('DD/MM/YYYY')} ~ ${
                  commissionTier.effectiveTo
                    ? moment(commissionTier.effectiveTo).format('DD/MM/YYYY')
                    : ''
                }`}
              </div>
            </div>
            <div className="commission-list__bottom">
              <div className="commission-list__btn-box">
                <If
                  condition={showElementByAuth(
                    platformEntity.COMMISSION_COMISSION_TIERS,
                    entityAction.UPDATE,
                  )}
                >
                  <button
                    className="listings-tab__edit-listing-btn"
                    onClick={() => {
                      this.props.openModal('edit', commissionTier);
                    }}
                  >
                    <EditOutlined
                      style={{
                        color: '#38b2a6',
                      }}
                    />
                  </button>
                </If>
                <If
                  condition={showElementByAuth(
                    platformEntity.COMMISSION_COMISSION_TIERS,
                    entityAction.CREATE,
                  )}
                >
                  <button
                    className={'listings-tab__copy-listing-btn commission-list__copy'}
                    onClick={() => {
                      this.props.openModal('copy', commissionTier);
                    }}
                  >
                    <CopyOutlined
                      style={{
                        color: '#38b2a6',
                      }}
                    />
                  </button>
                </If>
                <If
                  condition={showElementByAuth(
                    platformEntity.COMMISSION_COMISSION_TIERS,
                    entityAction.DELETE,
                  )}
                >
                  <Popconfirm
                    placement="top"
                    title={t('cms:cms.properties.edit.commission.delete_commission_hint')}
                    onConfirm={e => {
                      e.stopPropagation();
                      this.handleDeleteCommission(commissionTier.id);
                    }}
                    onCancel={e => {
                      e.stopPropagation();
                    }}
                    okText={t('cms:cms.properties.edit.btn.yes')}
                    okType="danger"
                    cancelText={t('cms:cms.properties.edit.btn.no')}
                  >
                    <button className={'listings-tab__copy-listing-btn commission-list__delete'}>
                      <DeleteOutlined
                        style={{
                          color: '#38b2a6',
                        }}
                      />
                    </button>
                  </Popconfirm>
                </If>
                <If
                  condition={showElementByAuth(
                    platformEntity.COMMISSION_COMISSION_TIERS,
                    entityAction.READ,
                  )}
                >
                  <Button
                    type="link"
                    className="commission-list__view"
                    onClick={() => this.handleChangeModal(true, commissionTier)}
                  >
                    {t('cms:cms.properties.edit.review_modal.button.view_detail')}
                  </Button>
                </If>
              </div>
              <Tooltip
                title={this.showUpdate(commissionTier.updatedAt)}
                getPopupContainer={() => this.timeDom[index]}
                placement="topRight"
                arrowPointAtCenter
              >
                <div
                  className="commission-list__update"
                  ref={node => {
                    this.timeDom[index] = node;
                  }}
                >
                  <SvgFormat
                    className="property-card__icon property-card__update-icon"
                    type="time"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        </For>
        <Modal
          visible={showModal}
          footer={
            <Button type="primary" onClick={() => this.handleChangeModal(false)}>
              {t('cms:cms.landlord.modal.confirm.button')}
            </Button>
          }
          onCancel={() => this.handleChangeModal(false)}
          width={880}
        >
          <div className="commission-list__modal">
            <p className="commission-list__modal-title">
              {t('cms:cms.property.commission.create.create_commission_modal.edit.title')}
            </p>
            <For of={commissionDetails} each="detailItem" index="index">
              <p key={index} className="commission-list__modal-subtitle">
                {t(detailItem.name)}
              </p>
              <For of={detailItem.children} each="detailChildren" index="index">
                <Choose>
                  <When condition={detailChildren[1] === 'singleLine'}>
                    <p key={index}>
                      {detailChildren[2] ? t(detailChildren[0]) : detailChildren[0]}
                    </p>
                  </When>
                  <Otherwise>
                    <span key={index} className="commission-list__modal-detail">
                      {t(detailChildren[0])}:&nbsp;&nbsp;{detailChildren[1]}
                    </span>
                  </Otherwise>
                </Choose>
              </For>
            </For>
          </div>
        </Modal>
      </div>
    );
  }
}

CommissionList.propTypes = {
  openModal: PropTypes.func.isRequired,
  commissionTier: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  deleteCommission: PropTypes.func.isRequired,
};

CommissionList.defaultProps = {
  openModal: () => {},
  commissionTier: [],
  t: () => {},
  currency: '',
  deleteCommission: () => {},
};
